// Formatação de duração para display (MET-01/02/03/05/25 e afins).
//
// Regra dura do módulo de métricas: "sem amostra" é `null`, NUNCA `0`. Foi
// exatamente o defeito do `estimateAvgResponseMs` do back — um `catch { return 0 }`
// virou "tempo médio: 0ms" por meses. Aqui `null`/`undefined`/`NaN` viram `—`,
// que é visivelmente diferente de "0s".
//
// Só apresentação: nenhuma decisão de negócio olha o retorno destas funções.

/** Traço exibido quando não há amostra. Em-dash, não hífen. */
export const NO_DATA = '—';

const SEC = 1000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export type DurationStyle =
  /** `1h 12m`, `2m 30s`, `45s` — duas unidades no máximo. Default. */
  | 'short'
  /** `1 hora e 12 minutos` — para leitores de tela e resumos de gráfico. */
  | 'long'
  /** `01:12:30` / `02:30` — contadores regressivos e cronômetros. */
  | 'clock';

export interface FormatDurationOptions {
  style?: DurationStyle;
  /** Texto exibido quando não há valor. Default `'—'`. */
  emptyLabel?: string;
}

const LONG_UNITS = {
  d: ['dia', 'dias'],
  h: ['hora', 'horas'],
  m: ['minuto', 'minutos'],
  s: ['segundo', 'segundos'],
} as const;

type UnitKey = keyof typeof LONG_UNITS;

interface Part {
  unit: UnitKey;
  value: number;
}

/**
 * Quebra em no máximo duas unidades, da mais significativa para a seguinte.
 * A unidade menor some quando é zero (`2h`, não `2h 00m`).
 */
function split(ms: number): Part[] {
  if (ms >= DAY) {
    return trimTail([
      { unit: 'd', value: Math.floor(ms / DAY) },
      { unit: 'h', value: Math.floor((ms % DAY) / HOUR) },
    ]);
  }
  if (ms >= HOUR) {
    return trimTail([
      { unit: 'h', value: Math.floor(ms / HOUR) },
      { unit: 'm', value: Math.floor((ms % HOUR) / MIN) },
    ]);
  }
  if (ms >= MIN) {
    return trimTail([
      { unit: 'm', value: Math.floor(ms / MIN) },
      { unit: 's', value: Math.floor((ms % MIN) / SEC) },
    ]);
  }
  return [{ unit: 's', value: Math.floor(ms / SEC) }];
}

function trimTail(parts: Part[]): Part[] {
  return parts[1].value === 0 ? [parts[0]] : parts;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function clockOf(ms: number): string {
  const total = Math.floor(ms / SEC);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/**
 * Duração em milissegundos → texto curto em PT-BR.
 *
 * `null`/`undefined`/`NaN`/`Infinity` ⇒ `'—'` (sem amostra). Valor negativo é
 * tratado como `0` — quem chama é que decide o que dizer sobre "já venceu"
 * (a janela de 24h expirada mostra "Expirada", não "-3m").
 */
export function formatDuration(
  ms: number | null | undefined,
  { style = 'short', emptyLabel = NO_DATA }: FormatDurationOptions = {},
): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return emptyLabel;

  const value = Math.max(0, ms);
  if (style === 'clock') return clockOf(value);

  const parts = split(value);
  if (style === 'long') {
    return parts
      .map((p) => `${p.value} ${LONG_UNITS[p.unit][p.value === 1 ? 0 : 1]}`)
      .join(' e ');
  }
  return parts.map((p) => `${p.value}${p.unit}`).join(' ');
}

/**
 * Mesma coisa a partir de segundos — os DTOs de métrica expõem tanto `*Ms`
 * quanto `*Sec` (`firstResponseSec`, `resolutionSec`), e converter na mão em
 * cada call site é onde nasce o `* 1000` esquecido.
 */
export function formatDurationSec(
  sec: number | null | undefined,
  options?: FormatDurationOptions,
): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec)) {
    return options?.emptyLabel ?? NO_DATA;
  }
  return formatDuration(sec * SEC, options);
}
